import { Handle, Position } from '@xyflow/react'
import { Database, X, FileJson, File, Link2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface DataNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

const sourceTypeIcons: Record<string, React.ReactNode> = {
  database: <Database size={14} />,
  file: <File size={14} />,
  api: <Link2 size={14} />,
  json: <FileJson size={14} />,
}

export default function DataNode({ data, selected, id }: DataNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const sourceType = String(data.config?.type || 'unknown')
  const sourceIcon = sourceTypeIcons[sourceType] || <Database size={14} />

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-card min-w-48 transition-all ${
        selected
          ? 'border-success ring-2 ring-success/30'
          : 'border-success/30 hover:border-success'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Database size={16} className="text-success flex-shrink-0" />
          <div className="font-bold text-sm text-foreground truncate">
            {data.label || t('build.source')}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-error-soft rounded text-error"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-muted-foreground mb-2 bg-card rounded p-2">
        {data.config?.source && (
          <div className="truncate text-foreground">
            {String(data.config.source)}
          </div>
        )}
        {data.config?.description && (
          <div className="text-muted-foreground text-xs truncate">
            {String(data.config.description)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 bg-success-soft text-success rounded flex items-center gap-1">
          {sourceIcon as React.ReactNode}
          {sourceType}
        </span>
        {data.config?.recordCount && (
          <span className="px-2 py-1 bg-info-soft text-info rounded">
            {t('build.recordsCount', { count: Number(data.config.recordCount) })}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
